/**
 * Marketo: Emails
 *
 * This file's entire purpose is to move the blacklisted emails lists into a
 * separate file, to stop it bloating other files.
 *
 * @example import { MRKTOEmails } from '../common/marketo/mrkto-emails.js';
 *
 * @module marketo/MRKTOEmails
 */


// ƒ
// ============================================================================


/**
 * Checks if the provided email address uses a free domain name
 *
 * @param   {string}   email     Email address to check
 * @param   {boolean}  useShort  If true, uses the short domain name list (not sure why this would be needed)
 *
 * @return  {boolean}            True if True if the email uses a listed domain, false otherwise
 */
function isEmailAddressFree( email, useShort = false )
{
	// This code was originally in l-marketo.js, and has not been changed since

	const domains = ( useShort ) ? getFreeEmailDomainsShort() : getFreeEmailDomains();

	return domains
		.map(function (domain)
		{
			return new RegExp("@" + domain.replace(".", "\\.") + "$", "i");
		})
		.some(function (reDomain)
		{
			return reDomain.test(email);
		});
}


/**
 * Same as `getFreeEmailDomains`, but the list is much shorter. (Not sure why
 * both a short and a long list was needed, that's just how the code was set up
 * before this func was created)
 *
 * @return  {Array<String>}  Array of email domain names
 */
function getFreeEmailDomainsShort()
{
	return [
		"126.com",
		"126.com",
		"139.com",
		"139.com",
		"163.com",
		"163.com",
		"189.cn",
		"189.com",
		"189.com",
		"foxmail.com",
		"mail.ru",
		"pp.com",
		"pp.com",
		"qq.cim",
		"qq.cim",
		"qq.cm",
		"qq.co",
		"qq.co",
		"qq.com!",
		"qq.com.",
		"qq.com",
		"qq.com",
		"qq.con",
		"qq.con",
		"qq.om",
		"qq.om",
		"qqq.com",
		"sina.cn",
		"sina.com",
		"sina.com",
		"yeah.net",
	];
}


/**
 * Get a list of free email domains (eg "gmail.com")
 *
 * @return  {Array<String>}  Array of email domain names
 */
function getFreeEmailDomains()
{
	return [
		"2-mail.com",
		"4email.net",
		"16mail.com",
		"50mail.com",
		"123mail.org",
		"126.com",
		"126.com",
		"139.com",
		"139.com",
		"150mail.com",
		"150ml.com",
		"163.com",
		"163.com",
		"189.cn",
		"189.com",
		"189.com",
		"airpost.net",
		"alice.it",
		"allmail.net",
		"aol.com",
		"bestmail.us",
		"cluemail.com",
		"elitemail.org",
		"emailcorner.net",
		"emailengine.net",
		"emailengine.org",
		"emailgroups.net",
		"emailplus.org",
		"emailuser.net",
		"eml.cc",
		"f-m.fm",
		"fast-email.com",
		"fast-mail.org",
		"fastem.com",
		"fastemail.us",
		"fastemailer.com",
		"fastest.cc",
		"fastimap.com",
		"fastmail.cn",
		"fastmail.co.uk",
		"fastmail.com.au",
		"fastmail.com",
		"fastmail.de",
		"fastmail.es",
		"fastmail.fm",
		"fastmail.fr",
		"fastmail.im",
		"fastmail.in",
		"fastmail.jp",
		"fastmail.mx",
		"fastmail.net",
		"fastmail.nl",
		"fastmail.org",
		"fastmail.se",
		"fastmail.to",
		"fastmail.tw",
		"fastmail.us",
		"fastmailbox.net",
		"fastmessaging.com",
		"fea.st",
		"fmail.co.uk",
		"fmailbox.com",
		"fmgirl.com",
		"fmguy.com",
		"foxmail.com",
		"ftml.net",
		"gmail.co.uk",
		"gmail.com",
		"gmx.at",
		"gmx.de",
		"gmx.li",
		"gmx.net",
		"googlemail.com",
		"h-mail.us",
		"hailmail.net",
		"hotmail.co.uk",
		"hotmail.com",
		"imap-mail.com",
		"imap.cc",
		"imapmail.org",
		"inoutbox.com",
		"internet-e-mail.com",
		"internet-mail.org",
		"internetemails.net",
		"internetmailing.net",
		"jetemail.net",
		"justemail.net",
		"letterboxes.org",
		"live.com",
		"mail-central.com",
		"mail-page.com",
		"mail.ru",
		"mailandftp.com",
		"mailas.com",
		"mailbolt.com",
		"mailc.net",
		"mailcan.com",
		"mailforce.net",
		"mailftp.com",
		"mailhaven.com",
		"mailingaddress.org",
		"mailite.com",
		"mailmight.com",
		"mailnew.com",
		"mailsent.net",
		"mailservice.ms",
		"mailup.net",
		"mailworks.org",
		"ml1.net",
		"mm.st",
		"msn.com",
		"myfastmail.com",
		"mymacmail.com",
		"nospammail.net",
		"outlook.com",
		"ownmail.net",
		"petml.com",
		"postinbox.com",
		"postpro.net",
		"pp.com",
		"pp.com",
		"proinbox.com",
		"promessage.com",
		"qq.cim",
		"qq.cim",
		"qq.cm",
		"qq.co",
		"qq.co",
		"qq.com!",
		"qq.com.",
		"qq.com",
		"qq.com",
		"qq.con",
		"qq.con",
		"qq.om",
		"qq.om",
		"qqq.com",
		"realemail.net",
		"reallyfast.biz",
		"reallyfast.info",
		"rushpost.com",
		"sent.as",
		"sent.at",
		"sent.com",
		"sina.cn",
		"sina.com",
		"sina.com",
		"speedpost.net",
		"speedymail.org",
		"ssl-mail.com",
		"swift-mail.com",
		"the-fastest.net",
		"the-quickest.com",
		"theinternetemail.com",
		"veryfast.biz",
		"veryspeedy.net",
		"warpmail.net",
		"xsmail.com",
		"yaho.com",
		"yahoo.ca",
		"yahoo.co.in",
		"yahoo.co.jp",
		"yahoo.co.kr",
		"yahoo.co.nz",
		"yahoo.co.uk",
		"yahoo.com.ar",
		"yahoo.com.au",
		"yahoo.com.br",
		"yahoo.com.cn",
		"yahoo.com.hk",
		"yahoo.com.is",
		"yahoo.com.mx",
		"yahoo.com.ru",
		"yahoo.com.sg",
		"yahoo.com",
		"yahoo.de",
		"yahoo.dk",
		"yahoo.es",
		"yahoo.fr",
		"yahoo.ie",
		"yahoo.it",
		"yahoo.jp",
		"yahoo.ru",
		"yahoo.se",
		"yeah.net",
		"yepmail.net",
		"your-mail.com",
	];
}


// Exports
// ============================================================================

export const MRKTOEmails = { getFreeEmailDomains, getFreeEmailDomainsShort, isEmailAddressFree };
